import { useMemo, useRef, useState } from 'react'

import { RecordType } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { SalariesDataCollectionDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import app from 'modules/core/store/models/app'

import {
  NODE_UUID_TYPES,
  findNodeByUuid,
  formatUuidToNodeType,
  getAllSalariesFromChildrenAttributes,
  getChildNodeType,
  getNodeType,
  getSalariesFromAttributes,
  normalizeNodeType,
  parseTreeNodes,
} from 'modules/job_catalog/lib/nodesUtils'
import { Salary, Workplace } from 'modules/job_catalog/lib/salary'

import { useSalaryActions } from './useSalaryActions'

const PER_PAGE = 10
const INITIAL_NON_LEVEL_CHILDREN_FETCH_SIZE = 1000

export type SalaryRecord = RecordType & Salary

export const useSalaryPage = (nodeUuid: string, canManageTree: boolean) => {
  const nodeType = useMemo(() => getNodeType(nodeUuid), [nodeUuid])
  const childType = getChildNodeType(nodeUuid)
  const isLevel = useMemo(() => nodeType === NODE_UUID_TYPES.LEVEL, [nodeType])

  const [nodeName, setNodeName] = useState<string>('')
  const [showSalaryModal, setShowSalaryModal] = useState(false)
  const [editSalary, setEditedSalary] = useState<Salary>()
  const [salaries, setSalaries] = useState<Salary[]>([])
  const [selectedWorkplaces, setSelectedWorkplaces] = useState<Workplace[]>([])
  const [refreshVersion, setRefreshVersion] = useState(0)
  const paginationRef = useRef<{ page: number; perPage: number }>({ page: 1, perPage: PER_PAGE })
  const nonLevelSalariesRef = useRef<Salary[]>([])
  const nonLevelChildrenPerPageRef = useRef(INITIAL_NON_LEVEL_CHILDREN_FETCH_SIZE)
  const warnedNonLevelTruncationRef = useRef(false)

  const { handleCreateSalary, handleUpdateSalary, handleDeleteSalary } = useSalaryActions()

  const closeSalaryModal = () => {
    setEditedSalary(undefined)
    setShowSalaryModal(false)
  }

  const openCreateSalaryModal = () => {
    track(trackingEvents.jobCatalog.createSalaryRangeStart, {
      event_category: 'Organizations',
      node_id: nodeUuid,
      node_type: normalizeNodeType(nodeUuid),
      from: 'salaries_tab',
    })
    setEditedSalary(undefined)
    setShowSalaryModal(true)
  }

  const openEditSalaryModal = (salary: Salary) => {
    setEditedSalary(salary)
    setShowSalaryModal(true)
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: SalariesDataCollectionDocument,
      totalItemSummary: (totalItems: number) =>
        i18n.tp('job_catalog.nodes.salaries.total_item_summary', { count: totalItems }),
      mapData: (data) => {
        const treeNodes = parseTreeNodes(data.jobCatalog.treesConnection.nodes[0])
        const foundNode = findNodeByUuid(treeNodes, nodeUuid)
        if (foundNode) setNodeName(foundNode.name)

        if (isLevel) {
          const salaryAttributes = getSalariesFromAttributes(
            data.jobCatalog.nodeAttributesConnection?.nodes ?? []
          )
          setSalaries(salaryAttributes)
          setSelectedWorkplaces(getWorkplacesFromSalaries(salaryAttributes))
          return salaryAttributes.map((salary) => ({ ...salary }))
        }

        // Non-level: salaries are extracted client-side from child nodes.
        // The server returns ALL child nodes (unpaginated), so we must
        // paginate the flattened salary list on the client.
        const treeNodeConnection = data.jobCatalog.treeNodesConnection
        const childNodes = treeNodeConnection?.nodes ?? []
        const totalChildren = treeNodeConnection?.totalCount ?? childNodes.length

        // Try to fetch all children in one request when the backend allows it.
        if (
          totalChildren > childNodes.length &&
          totalChildren > nonLevelChildrenPerPageRef.current
        ) {
          nonLevelChildrenPerPageRef.current = totalChildren
          setRefreshVersion((currentVersion) => currentVersion + 1)
        }

        // If backend still caps results, surface that data may be incomplete.
        if (
          totalChildren > childNodes.length &&
          totalChildren <= nonLevelChildrenPerPageRef.current &&
          !warnedNonLevelTruncationRef.current
        ) {
          warnedNonLevelTruncationRef.current = true
          app.setFlash({
            type: 'wrong',
            message:
              'This node has too many children to load all salaries at once. Salary totals may be partial.',
          })
        }

        const allSalaries = getMergedAndSortedSalaries(childNodes)
        nonLevelSalariesRef.current = allSalaries
        setSalaries(allSalaries)
        setSelectedWorkplaces(getWorkplacesFromSalaries(allSalaries))

        const { page, perPage } = paginationRef.current
        const start = (page - 1) * perPage
        const pageSlice = allSalaries.slice(start, start + perPage)
        return pageSlice.map((salary) => ({ ...salary }))
      },
      ...(isLevel && {
        primaryActions:
          salaries.length && canManageTree
            ? () => ({
                label: i18n.t('job_catalog.nodes.actions.new'),
                onClick: openCreateSalaryModal,
                icon: Plus,
              })
            : undefined,
        itemActions: (salary) =>
          canManageTree
            ? [
                {
                  label: i18n.t('job_catalog.nodes.salaries.edit'),
                  onClick: async () => {
                    if (!recordIsSalary(salary)) return
                    openEditSalaryModal(salary)
                  },
                },
                {
                  label: i18n.t('job_catalog.nodes.salaries.delete'),
                  onClick: async () => {
                    if (!recordIsSalary(salary)) return
                    await handleDeleteSalaryAndRefresh(salary, nodeUuid)
                  },
                },
              ]
            : [],
      }),
      mapPagination: (data) => ({
        total: isLevel
          ? (data.jobCatalog.nodeAttributesConnection?.totalCount ?? 0)
          : nonLevelSalariesRef.current.length ||
            getMergedAndSortedSalaries(data.jobCatalog.treeNodesConnection?.nodes ?? []).length,
      }),
      perPage: PER_PAGE,
      fetchPolicy: isLevel ? 'cache-and-network' : 'cache-first',
      includePaginationVars: isLevel,
      mapVariables: ({ pagination }) => {
        paginationRef.current = {
          page: pagination?.currentPage ?? 1,
          perPage: pagination?.perPage ?? PER_PAGE,
        }
        return {
          nodeUuid: nodeUuid,
          childType: formatUuidToNodeType(childType || ''),
          isLevel,
          allChildrenPerPage: nonLevelChildrenPerPageRef.current,
        }
      },
    },
    [nodeUuid, childType, isLevel, refreshVersion, canManageTree]
  )

  const refreshSalaries = () => setRefreshVersion((currentVersion) => currentVersion + 1)

  const handleCreateSalaryAndRefresh = async (salary: Salary, uuid: string) => {
    const success = await handleCreateSalary(salary, uuid)
    if (success) refreshSalaries()
    return success
  }

  const handleUpdateSalaryAndRefresh = async (salary: Salary, uuid: string) => {
    const success = await handleUpdateSalary(salary, uuid)
    if (success) refreshSalaries()
    return success
  }

  const handleDeleteSalaryAndRefresh = async (salary: Salary, uuid: string) => {
    const success = await handleDeleteSalary(salary, uuid)
    if (success) refreshSalaries()
    return success
  }

  const hasAllWorkplacesSalary = useMemo(() => {
    return salaries.some((salary) => !salary.workplaces || salary.workplaces.length === 0)
  }, [salaries])

  return {
    source,
    isLevel,
    nodeName,
    nodeType,
    selectedWorkplaces,
    hasAllWorkplacesSalary,
    showSalaryModal,
    setShowSalaryModal,
    closeSalaryModal,
    openCreateSalaryModal,
    editSalary,
    handleCreateSalary: handleCreateSalaryAndRefresh,
    handleUpdateSalary: handleUpdateSalaryAndRefresh,
    handleDeleteSalary: handleDeleteSalaryAndRefresh,
  }
}

export const recordIsSalary = (record: RecordType): record is SalaryRecord =>
  record.id !== undefined

/**
 * Groups salaries across all child nodes by workplace-set + currency + frequency,
 * then computes a single aggregated min-max range per group.
 *
 * Unlike `summarizeSalaries` (which converts all amounts to yearly), this
 * preserves the original periodicity — all salaries in a group already share
 * the same frequency because we group by it.
 *
 * Example: two "All workplaces / EUR / Yearly" rows (€20k-€60k and €40k-€75k)
 * merge into one row showing €20k-€75k (min of mins, max of maxes).
 */
export const mergeSalariesByWorkplaceAndCurrency = (salaries: Salary[]): Salary[] => {
  const groups = new Map<string, Salary[]>()

  for (const salary of salaries) {
    const workplaceKey =
      salary.workplaces && salary.workplaces.length > 0
        ? [...salary.workplaces]
            .map((w) => w.id)
            .sort()
            .join(',')
        : 'all'
    const key = `${workplaceKey}|${salary.currency}|${salary.periodicity ?? 'yearly'}`

    const existing = groups.get(key)
    if (existing) {
      existing.push(salary)
    } else {
      groups.set(key, [salary])
    }
  }

  const merged: Salary[] = []
  for (const [groupKey, groupSalaries] of groups.entries()) {
    const first = groupSalaries[0]
    if (!first) continue

    const hasRangeSalary = groupSalaries.some((salary) => salary.min != null || salary.max != null)
    if (!hasRangeSalary && groupSalaries.length === 1 && first.gross != null) {
      merged.push({
        ...first,
        periodicity: first.periodicity ?? 'yearly',
        id: first.id ?? buildSyntheticSalaryId(first),
      })
      continue
    }

    let minAmount = Infinity
    let maxAmount = -Infinity

    for (const s of groupSalaries) {
      if (s.min != null && s.min < minAmount) minAmount = s.min
      if (s.max != null && s.max > maxAmount) maxAmount = s.max
      if (s.gross != null && s.gross < minAmount) minAmount = s.gross
      if (s.gross != null && s.gross > maxAmount) maxAmount = s.gross
    }

    // Skip groups where no numeric values were found
    if (!isFinite(minAmount) || !isFinite(maxAmount)) continue

    const periodicity = first.periodicity ?? 'yearly'
    merged.push({
      id: `merged|${groupKey}`,
      nodeName: first.nodeName,
      nodeId: first.nodeId,
      currency: first.currency,
      periodicity,
      workplaces: first.workplaces,
      min: minAmount,
      max: maxAmount,
    })
  }
  return merged
}

export const getMergedAndSortedSalaries = (
  nodes: Array<{ id?: string | null; name?: string | null; attributesJson?: string | null }> = []
) => {
  const normalizedNodes = nodes.map((node) => ({
    id: node.id ?? '',
    name: node.name,
    attributesJson: node.attributesJson,
  }))

  return sortSalariesForDisplay(
    mergeSalariesByWorkplaceAndCurrency(getAllSalariesFromChildrenAttributes(normalizedNodes))
  )
}

const sortSalariesForDisplay = (salaries: Salary[]): Salary[] => {
  return [...salaries].sort((left, right) => {
    // Sort by workplace label: "all workplaces" first, then alphabetically
    const leftHasWorkplaces = (left.workplaces?.length ?? 0) > 0
    const rightHasWorkplaces = (right.workplaces?.length ?? 0) > 0
    if (!leftHasWorkplaces && rightHasWorkplaces) return -1
    if (leftHasWorkplaces && !rightHasWorkplaces) return 1

    const leftWorkplace = left.workplaces?.map((w) => w.name).join(', ') ?? ''
    const rightWorkplace = right.workplaces?.map((w) => w.name).join(', ') ?? ''
    const byWorkplace = leftWorkplace.localeCompare(rightWorkplace, undefined, {
      sensitivity: 'base',
    })
    if (byWorkplace !== 0) return byWorkplace

    // Then by currency
    return (left.currency ?? '').localeCompare(right.currency ?? '', undefined, {
      sensitivity: 'base',
    })
  })
}

const buildSyntheticSalaryId = (salary: Salary) => {
  const workplaceKey =
    salary.workplaces && salary.workplaces.length > 0
      ? salary.workplaces
          .map((workplace) => workplace.id)
          .sort()
          .join(',')
      : 'all'

  const periodicity = salary.periodicity ?? 'yearly'
  return `${workplaceKey}|${salary.currency ?? 'unknown'}|${periodicity}`
}

const getWorkplacesFromSalaries = (salaries: Salary[]) => {
  const workplaces = new Set<Workplace>()
  salaries.forEach((salary) => {
    salary.workplaces?.forEach((workplace) => workplaces.add(workplace))
  })
  return Array.from(workplaces)
}
